/**
 * ScreenplayEditor v2 — single-contentEditable architecture.
 *
 * Why: per-line contentEditables block native multi-line selection and
 * make mid-element Enter awkward. By using ONE contentEditable container
 * with child <div> lines, the browser handles drag-select, copy, paste,
 * and selection across lines naturally. We intercept only structural keys.
 *
 * Pattern:
 *   - React renders an empty contentEditable on mount.
 *   - We imperatively populate / mutate the DOM via refs.
 *   - On every `input` event we normalize the DOM (ensure every direct
 *     child is a `<div data-element-id data-element-type>`), then read it
 *     back into `stateRef` and notify the parent via `onChange`.
 *   - Parent only re-loads the editor via the imperative `loadElements`
 *     method (for script load / undo / redo).
 *
 * Public imperative API (via ref):
 *   loadElements(elements)  — replace the entire editor contents
 *   getElements()           — read current elements
 *   undo() / redo()         — history navigation
 *   updateElementType(id, type) — set the type of a specific line
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  detectElementType,
  nextDefaultType,
  cycleType,
  ELEMENT_LABELS,
  ELEMENT_ORDER,
  computeSceneNumbers,
} from "@/lib/screenplayDetector";
import { Search, X, ArrowUp, ArrowDown, CaseSensitive, Replace as ReplaceIcon, RefreshCw, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { MarkdownLite } from "@/components/MarkdownLite";
import { paginatePrint } from "@/lib/pageCount";

const PLACEHOLDERS = {
  scene_heading: "INT. LOCATION — DAY",
  action: "Action / Dialogue",
  character: "CHARACTER NAME",
  dialogue: "What they say…",
  parenthetical: "(beat)",
  transition: "CUT TO:",
  shot: "CLOSE ON…",
  subheader: "Subheader",
  section: "Section break",
  lyric: "♪ lyric",
  note: "[[note]]",
};

const HISTORY_LIMIT = 250;
const HISTORY_DEBOUNCE_MS = 700;

function uid() {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36).slice(-4);
}

function deepCloneElements(els) {
  return els.map((e) => ({ ...e }));
}

// --- Selection / caret helpers ---------------------------------------------

function findScriptLineAncestor(node) {
  while (node && node !== document.body) {
    if (node.nodeType === 1 && node.classList?.contains("script-line")) return node;
    node = node.parentNode;
  }
  return null;
}

function getRelativeOffset(lineNode, container, offset) {
  if (!lineNode) return 0;
  try {
    const range = document.createRange();
    range.setStart(lineNode, 0);
    range.setEnd(container, offset);
    return range.toString().length;
  } catch {
    return 0;
  }
}

function setCaretAt(lineNode, targetOffset) {
  if (!lineNode) return;
  lineNode.focus();
  const sel = window.getSelection();
  const range = document.createRange();
  if (!lineNode.firstChild) {
    range.setStart(lineNode, 0);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    return;
  }
  const walker = document.createTreeWalker(lineNode, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let node;
  while ((node = walker.nextNode())) {
    const len = node.textContent.length;
    if (acc + len >= targetOffset) {
      range.setStart(node, Math.max(0, targetOffset - acc));
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    acc += len;
  }
  range.selectNodeContents(lineNode);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function getCurrentLineNode() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  return findScriptLineAncestor(sel.getRangeAt(0).startContainer);
}

// --- DOM <-> state ---------------------------------------------------------

function buildLineDiv(el, doc) {
  const div = doc.createElement("div");
  div.dataset.elementId = el.id;
  div.dataset.elementType = el.type;
  if (el.locked) div.dataset.locked = "1";
  div.className = `script-line el-${el.type}`;
  // Use textContent (not innerText) — preserves explicit text, no formatting weirdness
  div.textContent = el.text || "";
  return div;
}

// ---------------------------------------------------------------------------

export const ScreenplayEditor = forwardRef(function ScreenplayEditor(
  { initialElements, showPrompts = true, showPageBreaks = true, showSceneNumbers = false, annotations = [], onRereadAnnotation, mobileFormat = false, hideBadge = false, readOnly = false, onChange, onSave, onCursorMove, onPageInfo },
  ref
) {
  const containerRef = useRef(null);
  const stateRef = useRef(initialElements && initialElements.length ? initialElements : [{ id: uid(), type: "scene_heading", text: "", locked: false }]);
  const isMountedRef = useRef(false);
  const skipNextInputRef = useRef(false);
  const historyRef = useRef({ past: [], future: [] });
  const historyDebounceRef = useRef(null);
  const [sceneCount, setSceneCount] = useState(0);
  const [currentLineType, setCurrentLineType] = useState(stateRef.current[0]?.type || "scene_heading");
  const [activeLineId, setActiveLineId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [searchMatches, setSearchMatches] = useState([]);
  const [searchActive, setSearchActive] = useState(0);
  const searchInputRef = useRef(null);
  const showPromptsRef = useRef(showPrompts);
  showPromptsRef.current = showPrompts;

  // ----- DOM rendering -----------------------------------------------------
  const renderToDom = useCallback((elements) => {
    const container = containerRef.current;
    if (!container) return;
    skipNextInputRef.current = true;
    container.innerHTML = "";
    const frag = document.createDocumentFragment();
    for (const el of elements) {
      frag.appendChild(buildLineDiv(el, document));
    }
    container.appendChild(frag);
    updateScenePlaceholders();
    queueMicrotask(() => { skipNextInputRef.current = false; });
  }, []);

  const updateScenePlaceholders = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const children = Array.from(container.children);
    let n = 0;
    const showPromptsNow = showPromptsRef.current;
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const type = child.dataset.elementType || "action";
      if (type === "scene_heading") {
        n += 1;
        child.dataset.sceneNumber = String(n);
      } else {
        delete child.dataset.sceneNumber;
      }
      // Placeholder logic — keep one for the only-empty-first-line case.
      const isEmpty = child.textContent === "";
      const isOnlyEmptyLine = children.length === 1 && isEmpty;
      const showPlaceholder = isEmpty && (showPromptsNow || isOnlyEmptyLine);
      child.dataset.empty = isEmpty ? "true" : "false";
      child.dataset.placeholder = showPlaceholder ? (PLACEHOLDERS[type] || "") : "";
    }
    setSceneCount(n);
  }, []);

  // Sync placeholder visibility when the toggle changes
  useEffect(() => {
    updateScenePlaceholders();
  }, [showPrompts, updateScenePlaceholders]);

  // ----- State read --------------------------------------------------------
  const readState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return [];
    const out = [];
    let prevType = "action";
    // Map of existing locked state by id
    const lockedById = new Map();
    for (const el of stateRef.current) lockedById.set(el.id, !!el.locked);

    const children = Array.from(container.children);
    for (const child of children) {
      if (child.nodeType !== 1) continue;
      if (child.tagName !== "DIV") {
        // Browser inserted a <br>, <span>, etc. Wrap into a div.
        const wrap = document.createElement("div");
        wrap.dataset.elementType = prevType;
        wrap.dataset.elementId = uid();
        wrap.className = `script-line el-${prevType}`;
        wrap.textContent = child.textContent || "";
        child.replaceWith(wrap);
        out.push({
          id: wrap.dataset.elementId,
          type: prevType,
          text: wrap.textContent,
          locked: false,
        });
        continue;
      }
      let id = child.dataset.elementId;
      if (!id) {
        id = uid();
        child.dataset.elementId = id;
      }
      let type = child.dataset.elementType;
      if (!type) {
        type = prevType;
        child.dataset.elementType = type;
        child.className = `script-line el-${type}`;
      }
      const locked = !!(lockedById.get(id) || child.dataset.locked === "1");
      const text = child.textContent || "";
      out.push({ id, type, text, locked });
      prevType = type;
    }
    if (out.length === 0) {
      // Ensure at least one element
      const div = document.createElement("div");
      const id = uid();
      div.dataset.elementId = id;
      div.dataset.elementType = "scene_heading";
      div.className = "script-line el-scene_heading";
      div.textContent = "";
      container.appendChild(div);
      out.push({ id, type: "scene_heading", text: "", locked: false });
    }
    return out;
  }, []);

  // ----- History (Undo/Redo) ---------------------------------------------
  const snapshotHistory = useCallback(() => {
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    historyDebounceRef.current = setTimeout(() => {
      const snap = deepCloneElements(stateRef.current);
      const last = historyRef.current.past[historyRef.current.past.length - 1];
      if (last && JSON.stringify(last) === JSON.stringify(snap)) return;
      historyRef.current.past.push(snap);
      if (historyRef.current.past.length > HISTORY_LIMIT) {
        historyRef.current.past.shift();
      }
      historyRef.current.future = [];
    }, HISTORY_DEBOUNCE_MS);
  }, []);

  const flushHistory = useCallback(() => {
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
      historyDebounceRef.current = null;
      const snap = deepCloneElements(stateRef.current);
      const last = historyRef.current.past[historyRef.current.past.length - 1];
      if (!last || JSON.stringify(last) !== JSON.stringify(snap)) {
        historyRef.current.past.push(snap);
        if (historyRef.current.past.length > HISTORY_LIMIT) {
          historyRef.current.past.shift();
        }
      }
    }
  }, []);

  const performUndo = useCallback(() => {
    flushHistory();
    if (historyRef.current.past.length < 2) return;
    const current = historyRef.current.past.pop();
    historyRef.current.future.push(current);
    const prior = historyRef.current.past[historyRef.current.past.length - 1];
    stateRef.current = deepCloneElements(prior);
    renderToDom(stateRef.current);
    onChange && onChange(stateRef.current);
    // Focus first line at end
    const first = stateRef.current[0];
    if (first) requestAnimationFrame(() => {
      const node = containerRef.current?.querySelector(`[data-element-id="${first.id}"]`);
      if (node) setCaretAt(node, (first.text || "").length);
    });
  }, [flushHistory, onChange, renderToDom]);

  const performRedo = useCallback(() => {
    flushHistory();
    if (historyRef.current.future.length === 0) return;
    const next = historyRef.current.future.pop();
    historyRef.current.past.push(next);
    stateRef.current = deepCloneElements(next);
    renderToDom(stateRef.current);
    onChange && onChange(stateRef.current);
  }, [flushHistory, onChange, renderToDom]);

  // ----- Imperative API ---------------------------------------------------
  useImperativeHandle(
    ref,
    () => ({
      loadElements(els) {
        const safe = els && els.length ? els : [{ id: uid(), type: "scene_heading", text: "", locked: false }];
        stateRef.current = deepCloneElements(safe);
        renderToDom(stateRef.current);
        historyRef.current = { past: [deepCloneElements(stateRef.current)], future: [] };
      },
      getElements() {
        return deepCloneElements(stateRef.current);
      },
      undo: performUndo,
      redo: performRedo,
      updateElementType(elementId, newType) {
        const idx = stateRef.current.findIndex((e) => e.id === elementId);
        if (idx < 0) return;
        stateRef.current[idx] = {
          ...stateRef.current[idx],
          type: newType,
          locked: true,
        };
        // Update DOM
        const node = containerRef.current?.querySelector(`[data-element-id="${elementId}"]`);
        if (node) {
          node.dataset.elementType = newType;
          node.dataset.locked = "1";
          node.className = `script-line el-${newType}`;
        }
        updateScenePlaceholders();
        snapshotHistory();
        onChange && onChange(stateRef.current);
        // Refocus
        if (node) setCaretAt(node, (stateRef.current[idx].text || "").length);
      },
      openSearch() {
        setSearchOpen(true);
        requestAnimationFrame(() => searchInputRef.current?.focus());
      },
      closeSearch() {
        setSearchOpen(false);
      },
    }),
    [performUndo, performRedo, renderToDom, snapshotHistory, updateScenePlaceholders, onChange]
  );

  // ----- Mount ------------------------------------------------------------
  useEffect(() => {
    if (isMountedRef.current) return;
    isMountedRef.current = true;
    renderToDom(stateRef.current);
    historyRef.current = { past: [deepCloneElements(stateRef.current)], future: [] };
  }, [renderToDom]);

  // ----- Active-line tracking ---------------------------------------------
  // Drives both the floating element badge and the pink highlight band. We
  // sync from the caret (selectionchange / input / click) AND imperatively
  // after structural edits (Enter split / Backspace merge) so the marker
  // never lags behind the caret.
  const syncActiveLine = useCallback((node) => {
    if (!node || !containerRef.current?.contains(node)) return;
    setActiveLineId(node.dataset.elementId || null);
    setCurrentLineType(node.dataset.elementType || "action");
  }, []);

  // ----- Input handler ----------------------------------------------------
  const handleInput = useCallback(() => {
    if (skipNextInputRef.current) return;
    const newState = readState();
    stateRef.current = newState;
    updateScenePlaceholders();
    onChange && onChange(newState);
    snapshotHistory();
    syncActiveLine(getCurrentLineNode());
  }, [readState, updateScenePlaceholders, onChange, snapshotHistory, syncActiveLine]);

  // ----- Cursor tracking --------------------------------------------------
  const handleSelectionChange = useCallback(() => {
    const node = getCurrentLineNode();
    if (node && containerRef.current?.contains(node)) {
      syncActiveLine(node);
      if (onCursorMove) {
        onCursorMove({ elementId: node.dataset.elementId, type: node.dataset.elementType || "action" });
      }
    }
  }, [onCursorMove, syncActiveLine]);

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, [handleSelectionChange]);

  // ----- Key handling -----------------------------------------------------
  const handleKeyDown = useCallback((e) => {
    // Find & replace bar (⌘/Ctrl+F) — works in read-only too.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
      e.preventDefault();
      setSearchOpen(true);
      requestAnimationFrame(() => searchInputRef.current?.focus());
      return;
    }
    if (readOnly) {
      // Allow navigation/selection/copy, block anything that mutates text.
      const allowed = e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End" ||
        e.key === "PageUp" || e.key === "PageDown" || e.key === "Tab" ||
        ((e.metaKey || e.ctrlKey) && ["a", "c"].includes(e.key.toLowerCase()));
      if (!allowed) e.preventDefault();
      return;
    }
    const sel = window.getSelection();
    if (!sel) return;
    const container = containerRef.current;
    if (!container) return;

    // Undo / Redo
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
      e.preventDefault();
      performUndo();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && ((e.shiftKey && e.key.toLowerCase() === "z") || e.key.toLowerCase() === "y")) {
      e.preventDefault();
      performRedo();
      return;
    }

    // Save
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      flushHistory();
      onSave && onSave();
      return;
    }

    // ⌘/Ctrl+1..7 force element type for the current line
    if ((e.metaKey || e.ctrlKey) && /^[1-7]$/.test(e.key)) {
      e.preventDefault();
      const lineNode = getCurrentLineNode();
      if (!lineNode || !container.contains(lineNode)) return;
      const map = { "1": "scene_heading", "2": "action", "3": "character",
                    "4": "dialogue", "5": "parenthetical", "6": "transition", "7": "shot" };
      const newType = map[e.key];
      const id = lineNode.dataset.elementId;
      const idx = stateRef.current.findIndex((x) => x.id === id);
      if (idx < 0) return;
      stateRef.current[idx] = { ...stateRef.current[idx], type: newType, locked: true };
      lineNode.dataset.elementType = newType;
      lineNode.dataset.locked = "1";
      lineNode.className = `script-line el-${newType}`;
      updateScenePlaceholders();
      setCurrentLineType(newType);
      snapshotHistory();
      onChange && onChange(stateRef.current);
      return;
    }

    // Enter: split line at caret into TWO elements
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const r = sel.rangeCount ? sel.getRangeAt(0) : null;
      if (!r) return;
      // If a non-collapsed selection exists, delete its content first
      if (!r.collapsed) {
        deleteSelectionContents(sel);
      }
      const lineNode = getCurrentLineNode();
      if (!lineNode || !container.contains(lineNode)) return;
      const id = lineNode.dataset.elementId;
      const idx = stateRef.current.findIndex((x) => x.id === id);
      if (idx < 0) return;

      const r2 = window.getSelection().getRangeAt(0);
      const cursorOffset = getRelativeOffset(lineNode, r2.startContainer, r2.startOffset);
      const fullText = lineNode.textContent || "";
      const before = fullText.substring(0, cursorOffset);
      const after = fullText.substring(cursorOffset);

      const current = stateRef.current[idx];
      const prev = idx > 0 ? stateRef.current[idx - 1] : null;
      const detectedType = current.locked
        ? current.type
        : detectElementType(before, prev?.type, prev?.text);

      const newId = uid();
      let newType;
      if (after.trim()) {
        newType = detectElementType(after, detectedType);
      } else {
        newType = nextDefaultType(detectedType);
      }

      // Mutate DOM
      lineNode.textContent = before;
      lineNode.dataset.elementType = detectedType;
      lineNode.className = `script-line el-${detectedType}`;

      const newDiv = buildLineDiv({ id: newId, type: newType, text: after }, document);
      if (lineNode.nextSibling) {
        container.insertBefore(newDiv, lineNode.nextSibling);
      } else {
        container.appendChild(newDiv);
      }

      // Mutate state
      stateRef.current = [
        ...stateRef.current.slice(0, idx),
        { ...current, type: detectedType, text: before },
        { id: newId, type: newType, text: after, locked: false },
        ...stateRef.current.slice(idx + 1),
      ];

      updateScenePlaceholders();
      // Focus the new element at offset 0
      requestAnimationFrame(() => setCaretAt(newDiv, 0));
      setActiveLineId(newId);
      setCurrentLineType(newType);
      snapshotHistory();
      onChange && onChange(stateRef.current);
      return;
    }

    // Tab: cycle element type of current line
    if (e.key === "Tab") {
      e.preventDefault();
      const lineNode = getCurrentLineNode();
      if (!lineNode || !container.contains(lineNode)) return;
      const id = lineNode.dataset.elementId;
      const idx = stateRef.current.findIndex((x) => x.id === id);
      if (idx < 0) return;
      const newType = cycleType(stateRef.current[idx].type);
      stateRef.current[idx] = { ...stateRef.current[idx], type: newType, locked: true };
      lineNode.dataset.elementType = newType;
      lineNode.dataset.locked = "1";
      lineNode.className = `script-line el-${newType}`;
      updateScenePlaceholders();
      setCurrentLineType(newType);
      snapshotHistory();
      onChange && onChange(stateRef.current);
      return;
    }

    // Backspace at start of NON-empty line → merge into previous
    if (e.key === "Backspace" && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (r.collapsed) {
        const lineNode = getCurrentLineNode();
        if (lineNode && container.contains(lineNode)) {
          const offset = getRelativeOffset(lineNode, r.startContainer, r.startOffset);
          const id = lineNode.dataset.elementId;
          const idx = stateRef.current.findIndex((x) => x.id === id);
          if (offset === 0 && idx > 0) {
            // Merge with previous element
            e.preventDefault();
            const prev = stateRef.current[idx - 1];
            const prevNode = container.querySelector(`[data-element-id="${prev.id}"]`);
            const prevText = prev.text || "";
            const myText = lineNode.textContent || "";
            const mergedText = prevText + myText;
            if (prevNode) {
              prevNode.textContent = mergedText;
            }
            lineNode.remove();
            stateRef.current = [
              ...stateRef.current.slice(0, idx - 1),
              { ...prev, text: mergedText, locked: true },
              ...stateRef.current.slice(idx + 1),
            ];
            updateScenePlaceholders();
            if (prevNode) setCaretAt(prevNode, prevText.length);
            setActiveLineId(prev.id);
            setCurrentLineType(prev.type);
            snapshotHistory();
            onChange && onChange(stateRef.current);
            return;
          }
          // If only empty line in the script: keep one element, just clear it.
          if (offset === 0 && lineNode.textContent === "" && stateRef.current.length === 1) {
            e.preventDefault();
            return;
          }
        }
      }
    }

    // Otherwise: let the browser handle it. (Drag selection + delete works natively.)
  }, [
    performUndo, performRedo, flushHistory, onSave,
    updateScenePlaceholders, snapshotHistory, onChange, readOnly,
  ]);

  // ----- Paste handler: clean blank lines, repeated whitespace ------------
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = (e.clipboardData?.getData("text/plain") || "").replace(/\r\n?/g, "\n");
    if (!text) return;

    // Clean: collapse multiple consecutive blank lines, strip leading/trailing
    // blank lines, and squish runs of spaces (but preserve a single blank line
    // between paragraphs as our "newline within element"? No — each line
    // becomes a separate element. We just drop blank lines entirely.)
    const cleaned = text
      .split("\n")
      .map((l) => l.replace(/[ \t]+/g, " ").trim())
      .filter((l) => l.length > 0);

    if (cleaned.length === 0) return;

    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const r = sel.getRangeAt(0);
    if (!r.collapsed) deleteSelectionContents(sel);

    const lineNode = getCurrentLineNode();
    if (!lineNode || !containerRef.current.contains(lineNode)) return;
    const id = lineNode.dataset.elementId;
    const idx = stateRef.current.findIndex((x) => x.id === id);
    if (idx < 0) return;

    const r2 = window.getSelection().getRangeAt(0);
    const cursorOffset = getRelativeOffset(lineNode, r2.startContainer, r2.startOffset);
    const fullText = lineNode.textContent || "";
    const before = fullText.substring(0, cursorOffset);
    const after = fullText.substring(cursorOffset);

    // Single-line paste: just inline-merge
    if (cleaned.length === 1) {
      const merged = before + cleaned[0] + after;
      lineNode.textContent = merged;
      stateRef.current[idx] = { ...stateRef.current[idx], text: merged };
      updateScenePlaceholders();
      setCaretAt(lineNode, before.length + cleaned[0].length);
      snapshotHistory();
      onChange && onChange(stateRef.current);
      return;
    }

    // Multi-line paste: split into multiple elements
    const current = stateRef.current[idx];
    const prev = idx > 0 ? stateRef.current[idx - 1] : null;
    let prevType = prev?.type || null;

    // First chunk: merged into current element's "before" text
    const firstText = before + cleaned[0];
    const firstType = current.locked
      ? current.type
      : detectElementType(firstText, prevType);
    lineNode.textContent = firstText;
    lineNode.dataset.elementType = firstType;
    lineNode.className = `script-line el-${firstType}`;
    stateRef.current[idx] = { ...current, type: firstType, text: firstText };

    const newRecords = [];
    prevType = firstType;
    for (let i = 1; i < cleaned.length - 1; i += 1) {
      const t = cleaned[i];
      const detected = detectElementType(t, prevType);
      newRecords.push({ id: uid(), type: detected, text: t, locked: false });
      prevType = detected;
    }
    // Last chunk: append the "after" text
    const lastChunk = cleaned[cleaned.length - 1] + after;
    const lastType = detectElementType(lastChunk, prevType);
    const lastId = uid();
    newRecords.push({ id: lastId, type: lastType, text: lastChunk, locked: false });

    // Mutate DOM
    let anchor = lineNode;
    for (const rec of newRecords) {
      const div = buildLineDiv(rec, document);
      anchor.parentNode.insertBefore(div, anchor.nextSibling);
      anchor = div;
    }

    // Splice records into state
    stateRef.current = [
      ...stateRef.current.slice(0, idx + 1),
      ...newRecords,
      ...stateRef.current.slice(idx + 1),
    ];

    updateScenePlaceholders();
    const lastDiv = containerRef.current.querySelector(`[data-element-id="${lastId}"]`);
    if (lastDiv) {
      setCaretAt(lastDiv, cleaned[cleaned.length - 1].length);
    }
    snapshotHistory();
    onChange && onChange(stateRef.current);
  }, [updateScenePlaceholders, snapshotHistory, onChange]);

  // ----- Click on a line: surface its badge ------------------------------
  const handleClick = useCallback((e) => {
    syncActiveLine(findScriptLineAncestor(e.target));
  }, [syncActiveLine]);

  // ----- Floating element-type popover ----------------------------------
  // Renders next to the currently-active line. Click to change type.
  const [popoverState, setPopoverState] = useState(null);
  useEffect(() => {
    if (!activeLineId) {
      setPopoverState(null);
      return;
    }
    const container = containerRef.current;
    if (!container) return;
    const node = container.querySelector(`[data-element-id="${activeLineId}"]`);
    if (!node) {
      setPopoverState(null);
      return;
    }
    const updatePos = () => {
      setPopoverState({
        id: activeLineId,
        type: node.dataset.elementType,
        top: node.offsetTop,
      });
    };
    updatePos();
    const obs = new MutationObserver(updatePos);
    obs.observe(container, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", updatePos);
    container.addEventListener("scroll", updatePos);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", updatePos);
      container.removeEventListener("scroll", updatePos);
    };
  }, [activeLineId]);

  // ----- Find & Replace ---------------------------------------------------
  const buildMatches = useCallback((q, cs) => {
    if (!q) return [];
    let re;
    try { re = new RegExp(escapeRegExp(q), cs ? "g" : "gi"); } catch { return []; }
    const out = [];
    for (const el of stateRef.current) {
      const text = el.text || "";
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(text)) !== null) {
        out.push({ id: el.id, start: m.index, end: m.index + m[0].length });
        if (m.index === re.lastIndex) re.lastIndex += 1;
      }
    }
    return out;
  }, []);

  const applyHighlights = useCallback((matches, activeIdx) => {
    if (typeof CSS === "undefined" || !CSS.highlights || typeof Highlight === "undefined") return;
    const c = containerRef.current;
    if (!c) return;
    const all = new Highlight();
    const cur = new Highlight();
    matches.forEach((mt, i) => {
      const node = c.querySelector(`[data-element-id="${mt.id}"]`);
      const r = makeRange(node, mt.start, mt.end);
      if (!r) return;
      if (i === activeIdx) cur.add(r); else all.add(r);
    });
    CSS.highlights.set("sp-find", all);
    CSS.highlights.set("sp-find-current", cur);
  }, []);

  const clearHighlights = useCallback(() => {
    if (typeof CSS !== "undefined" && CSS.highlights) {
      CSS.highlights.delete("sp-find");
      CSS.highlights.delete("sp-find-current");
    }
  }, []);

  // Highlight + scroll to a match WITHOUT touching the document selection — that
  // is what used to steal focus from the search box and overwrite the script.
  const focusMatch = useCallback((idx, list, smooth = false) => {
    const matches = list || searchMatches;
    if (!matches.length) { clearHighlights(); setSearchActive(0); return; }
    const i = ((idx % matches.length) + matches.length) % matches.length;
    applyHighlights(matches, i);
    const mt = matches[i];
    const node = containerRef.current?.querySelector(`[data-element-id="${mt.id}"]`);
    if (node) node.scrollIntoView({ block: smooth ? "center" : "nearest", behavior: smooth ? "smooth" : "auto" });
    setSearchActive(i);
  }, [searchMatches, applyHighlights, clearHighlights]);

  const refreshSearch = useCallback((focusIdx = 0, smooth = false) => {
    const out = buildMatches(query, caseSensitive);
    setSearchMatches(out);
    if (!out.length) { setSearchActive(0); clearHighlights(); return; }
    focusMatch(focusIdx, out, smooth);
  }, [buildMatches, query, caseSensitive, focusMatch, clearHighlights]);

  const goNext = useCallback(() => focusMatch(searchActive + 1, searchMatches, true), [focusMatch, searchActive, searchMatches]);
  const goPrev = useCallback(() => focusMatch(searchActive - 1, searchMatches, true), [focusMatch, searchActive, searchMatches]);

  const replaceCurrent = useCallback(() => {
    if (readOnly || !searchMatches.length) return;
    const mt = searchMatches[searchActive];
    const idx = stateRef.current.findIndex((e) => e.id === mt.id);
    if (idx < 0) return;
    const text = stateRef.current[idx].text || "";
    const newText = text.slice(0, mt.start) + replaceTerm + text.slice(mt.end);
    stateRef.current[idx] = { ...stateRef.current[idx], text: newText };
    const node = containerRef.current?.querySelector(`[data-element-id="${mt.id}"]`);
    if (node) node.textContent = newText;
    updateScenePlaceholders();
    snapshotHistory();
    onChange && onChange(stateRef.current);
    requestAnimationFrame(() => refreshSearch(searchActive, false));
  }, [readOnly, searchMatches, searchActive, replaceTerm, updateScenePlaceholders, snapshotHistory, onChange, refreshSearch]);

  const replaceAll = useCallback(() => {
    if (readOnly || !query) return;
    const flags = caseSensitive ? "g" : "gi";
    const escaped = escapeRegExp(query);
    let count = 0;
    for (let i = 0; i < stateRef.current.length; i += 1) {
      const el = stateRef.current[i];
      const text = el.text || "";
      const found = text.match(new RegExp(escaped, flags));
      if (!found) continue;
      count += found.length;
      const newText = text.replace(new RegExp(escaped, flags), replaceTerm);
      stateRef.current[i] = { ...el, text: newText };
      const node = containerRef.current?.querySelector(`[data-element-id="${el.id}"]`);
      if (node) node.textContent = newText;
    }
    if (count > 0) {
      updateScenePlaceholders();
      snapshotHistory();
      onChange && onChange(stateRef.current);
    }
    setSearchMatches([]);
    setSearchActive(0);
    clearHighlights();
  }, [readOnly, query, caseSensitive, replaceTerm, updateScenePlaceholders, snapshotHistory, onChange, clearHighlights]);

  useEffect(() => {
    if (!searchOpen) return;
    refreshSearch(0, false);
  }, [query, caseSensitive, searchOpen]);

  useEffect(() => {
    if (!searchOpen) clearHighlights();
    return () => clearHighlights();
  }, [searchOpen, clearHighlights]);

  const closeSearchBar = useCallback(() => {
    setSearchOpen(false);
    clearHighlights();
    containerRef.current?.focus?.();
  }, [clearHighlights]);

  return (
    <div className={`screenplay-canvas-wrap ${mobileFormat ? "mobile-format" : ""}`}>
      {searchOpen && (
        <div
          className="screenplay-search-bar"
          data-testid="editor-search-bar"
          onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); closeSearchBar(); } }}
        >
          <div className="ssb-row">
            <Search size={14} className="ssb-icon" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.shiftKey ? goPrev() : goNext(); } }}
              placeholder="Find in script"
              data-testid="editor-search-input"
              className="ssb-input"
            />
            <span className="ssb-count" data-testid="editor-search-count">
              {searchMatches.length ? `${searchActive + 1}/${searchMatches.length}` : (query ? "0/0" : "")}
            </span>
            <button onClick={() => setCaseSensitive((c) => !c)} className={`ssb-btn ${caseSensitive ? "active" : ""}`} title="Match case" data-testid="editor-search-case"><CaseSensitive size={15} /></button>
            <button onClick={goPrev} className="ssb-btn" title="Previous (Shift+Enter)" data-testid="editor-search-prev" disabled={!searchMatches.length}><ArrowUp size={15} /></button>
            <button onClick={goNext} className="ssb-btn" title="Next (Enter)" data-testid="editor-search-next" disabled={!searchMatches.length}><ArrowDown size={15} /></button>
            <button onClick={closeSearchBar} className="ssb-btn" title="Close (Esc)" data-testid="editor-search-close"><X size={15} /></button>
          </div>
          {!readOnly && (
            <div className="ssb-row">
              <ReplaceIcon size={14} className="ssb-icon" />
              <input
                value={replaceTerm}
                onChange={(e) => setReplaceTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); replaceCurrent(); } }}
                placeholder="Replace with"
                data-testid="editor-replace-input"
                className="ssb-input"
              />
              <button onClick={replaceCurrent} className="ssb-text-btn" disabled={!searchMatches.length} data-testid="editor-replace-btn">Replace</button>
              <button onClick={replaceAll} className="ssb-text-btn" disabled={!query} data-testid="editor-replace-all-btn">All</button>
            </div>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className="screenplay-page"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        spellCheck
        data-testid="screenplay-page"
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleClick}
      />
      {popoverState && !hideBadge && (
        <ElementTypeBadge
          state={popoverState}
          onSelect={(newType) => {
            const idx = stateRef.current.findIndex((x) => x.id === popoverState.id);
            if (idx < 0) return;
            stateRef.current[idx] = { ...stateRef.current[idx], type: newType, locked: true };
            const node = containerRef.current?.querySelector(`[data-element-id="${popoverState.id}"]`);
            if (node) {
              node.dataset.elementType = newType;
              node.dataset.locked = "1";
              node.className = `script-line el-${newType}`;
              setCaretAt(node, (stateRef.current[idx].text || "").length);
            }
            updateScenePlaceholders();
            setCurrentLineType(newType);
            snapshotHistory();
            onChange && onChange(stateRef.current);
          }}
          onClose={() => {}}
        />
      )}
      <ActiveLineOverlay containerRef={containerRef} activeLineId={activeLineId} />
      {showSceneNumbers && <SceneNumberOverlay containerRef={containerRef} />}
      {annotations && annotations.length > 0 && <AnnotationOverlay containerRef={containerRef} annotations={annotations} onReread={onRereadAnnotation} />}
      {showPageBreaks && <PageBreakOverlay containerRef={containerRef} onPageInfo={onPageInfo} />}
    </div>
  );
});

function ElementTypeBadge({ state, onSelect, onClose }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="element-badge-floating"
      style={{ top: state.top }}
      data-testid={`element-badge-floating-${state.id}`}
    >
      <button
        type="button"
        className="element-badge-button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`element-badge-button-${state.id}`}
        title="Change line type"
      >
        {ELEMENT_LABELS[state.type] || state.type}
      </button>
      {open && (
        <div
          className="element-badge-menu"
          data-testid={`element-badge-menu-${state.id}`}
          onMouseLeave={() => setOpen(false)}
        >
          {ELEMENT_ORDER.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => { onSelect(t); setOpen(false); onClose(); }}
              className={t === state.type ? "active" : ""}
              data-testid={`element-badge-option-${t}-${state.id}`}
            >
              {ELEMENT_LABELS[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Subtle pink highlight band behind the active line (spans full page width). */
function ActiveLineOverlay({ containerRef, activeLineId }) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    const c = containerRef.current;
    if (!c || !activeLineId) { setRect(null); return; }
    const compute = () => {
      const node = c.querySelector(`[data-element-id="${activeLineId}"]`);
      if (!node) { setRect(null); return; }
      setRect({ top: node.offsetTop, height: node.offsetHeight });
    };
    compute();
    const obs = new MutationObserver(compute);
    obs.observe(c, { childList: true, subtree: true, characterData: true });
    const ro = new ResizeObserver(compute);
    ro.observe(c);
    window.addEventListener("resize", compute);
    return () => {
      obs.disconnect();
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [containerRef, activeLineId]);
  if (!rect) return null;
  return (
    <div
      className="active-line-band"
      style={{ top: rect.top, height: rect.height }}
      data-testid="active-line-highlight"
    />
  );
}

/** Floats scene numbers at the right margin, one per scene_heading line. */
function SceneNumberOverlay({ containerRef }) {
  const [scenes, setScenes] = useState([]);

  useEffect(() => {
    const update = () => {
      const c = containerRef.current;
      if (!c) return;
      const headings = c.querySelectorAll('[data-element-type="scene_heading"]');
      const list = [];
      headings.forEach((h, idx) => {
        list.push({
          id: h.dataset.elementId,
          n: idx + 1,
          top: h.offsetTop,
        });
      });
      setScenes(list);
    };
    update();
    const c = containerRef.current;
    if (!c) return;
    const obs = new MutationObserver(update);
    obs.observe(c, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", update);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [containerRef]);

  return (
    <>
      {scenes.map((s) => (
        <span
          key={`l-${s.id}`}
          className="scene-number-left"
          style={{ top: s.top }}
          data-testid={`scene-number-left-${s.n}`}
        >
          {s.n}
        </span>
      ))}
      {scenes.map((s) => (
        <span
          key={`r-${s.id}`}
          className="scene-number-right"
          style={{ top: s.top }}
          data-testid={`scene-number-${s.n}`}
        >
          {s.n}
        </span>
      ))}
    </>
  );
}

const ANNOTATION_META = {
  structure: { label: "Structure", color: "#3B7DD8" },
  character: { label: "Character", color: "#8B5CF6" },
  dialogue: { label: "Dialogue", color: "#12977A" },
  pacing: { label: "Pacing", color: "#E08A00" },
  logic: { label: "Logic / Continuity", color: "#D64545" },
};

/** Coverage margin notes — color-coded markers beside annotated lines. */
function AnnotationOverlay({ containerRef, annotations, onReread }) {
  const [markers, setMarkers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(null); // { id, scope }

  useEffect(() => {
    const update = () => {
      const c = containerRef.current;
      if (!c) return;
      const byEl = new Map();
      for (const a of annotations) {
        if (!a.element_id) continue;
        if (!byEl.has(a.element_id)) byEl.set(a.element_id, []);
        byEl.get(a.element_id).push(a);
      }
      const list = [];
      byEl.forEach((notes, elId) => {
        let node = null;
        try { node = c.querySelector(`[data-element-id="${elId}"]`); } catch { node = null; }
        if (!node) return;
        list.push({ elId, top: node.offsetTop, notes, text: (node.textContent || "").trim() });
      });
      list.sort((a, b) => a.top - b.top);
      setMarkers(list);
    };
    update();
    const c = containerRef.current;
    if (!c) return;
    const obs = new MutationObserver(update);
    obs.observe(c, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", update);
    return () => { obs.disconnect(); window.removeEventListener("resize", update); };
  }, [containerRef, annotations]);

  useEffect(() => {
    if (!openId) return;
    const onDown = (e) => {
      if (e.target.closest?.(".annotation-popover") || e.target.closest?.(".annotation-marker")) return;
      setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openId]);

  const doReread = async (a, scope = "scene") => {
    if (!onReread || busy) return;
    setBusy({ id: a.id, scope });
    try {
      const updated = await onReread(a.id, scope);
      if (updated?.status === "resolved") toast.success("The reader signed off on this note.");
      else toast("The reader left fresh feedback on this passage.");
    } catch {
      toast.error("The reader couldn't re-read right now. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const open = markers.find((m) => m.elId === openId) || null;

  return (
    <>
      {markers.map((m) => {
        const openNotes = m.notes.filter((n) => n.status !== "resolved");
        const allResolved = openNotes.length === 0;
        const meta = ANNOTATION_META[(openNotes[0] || m.notes[0]).category] || ANNOTATION_META.structure;
        return (
          <button
            key={m.elId}
            type="button"
            className={`annotation-marker ${openId === m.elId ? "open" : ""} ${allResolved ? "resolved" : ""}`}
            style={{ top: m.top, backgroundColor: allResolved ? "#8A9482" : meta.color }}
            onClick={() => setOpenId((cur) => (cur === m.elId ? null : m.elId))}
            data-testid={`annotation-marker-${m.elId}`}
            title={allResolved ? "Reader's note — resolved" : `Reader's note — ${meta.label}`}
          >
            {allResolved ? "✓" : (m.notes.length > 1 ? m.notes.length : "")}
          </button>
        );
      })}
      {open && (
        <div className="annotation-popover" style={{ top: open.top + 24 }} data-testid="annotation-popover">
          <div className="annotation-popover-head">
            <span>Reader's note{open.notes.length > 1 ? "s" : ""}</span>
            <button type="button" onClick={() => setOpenId(null)} data-testid="annotation-popover-close" title="Close">
              <X size={13} />
            </button>
          </div>
          {open.notes.map((a) => {
            const meta = ANNOTATION_META[a.category] || ANNOTATION_META.structure;
            if (a.status === "resolved") {
              return (
                <div key={a.id} className="annotation-note resolved" data-testid={`annotation-note-${a.id}`}>
                  <span className="annotation-chip" style={{ backgroundColor: "#8A9482" }}>✓ Resolved · {meta.label}</span>
                  <p className="annotation-original">{a.note}</p>
                  {a.resolution && <p className="annotation-resolution">“{a.resolution}”</p>}
                </div>
              );
            }
            const changed = !!a.excerpt && open.text.slice(0, 100) !== a.excerpt;
            return (
              <div key={a.id} className="annotation-note" data-testid={`annotation-note-${a.id}`}>
                <span className="annotation-chip" style={{ backgroundColor: meta.color }}>{meta.label}</span>
                {a.revised && <span className="annotation-revised-tag">Re-read</span>}
                {a.reversal && <span className="annotation-reversal-tag">Reader reversed</span>}
                <MarkdownLite text={a.note} />
                {a.principle && <p className="annotation-principle">{a.principle}</p>}
                {changed && (
                  <div className="annotation-changed-hint" data-testid={`annotation-changed-${a.id}`}>
                    This line has changed since this note.
                  </div>
                )}
                {onReread && (
                  <div className="annotation-reread-row">
                    <button
                      type="button"
                      className="annotation-reread-btn"
                      onClick={() => doReread(a, "scene")}
                      disabled={!!busy}
                      title="The reader re-reads just this scene against the note"
                      data-testid={`annotation-reread-${a.id}`}
                    >
                      {busy?.id === a.id && busy?.scope === "scene" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                      {busy?.id === a.id && busy?.scope === "scene" ? "Re-reading…" : "Re-read this scene"}
                    </button>
                    <button
                      type="button"
                      className="annotation-reread-btn"
                      onClick={() => doReread(a, "script")}
                      disabled={!!busy}
                      title="For notes about setups and payoffs across scenes — the reader re-reads the entire script with this note in mind"
                      data-testid={`annotation-reread-full-${a.id}`}
                    >
                      {busy?.id === a.id && busy?.scope === "script" ? <Loader2 size={11} className="animate-spin" /> : <BookOpen size={11} />}
                      {busy?.id === a.id && busy?.scope === "script" ? "Reading the full script…" : "Re-read the whole script"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * PageBreakOverlay — draws print-accurate page boundaries and reports
 * current/total pages to the parent. Pagination is computed from the ELEMENTS
 * with the same math as the PDF exporter (see lib/pageCount.js), NOT from
 * on-screen pixel heights — so the count matches the exported PDF on any
 * screen size. Each boundary line also gets real vertical breathing room.
 */
const PAGE_GAP_PX = 48;     // breathing room rendered between pages

function PageBreakOverlay({ containerRef, onPageInfo }) {
  const [breaks, setBreaks] = useState([]);
  const totalRef = useRef(1);
  const endIdsRef = useRef([]);

  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    const GAP = PAGE_GAP_PX;

    const clearGaps = () => {
      c.querySelectorAll(".script-line.page-end").forEach((n) => {
        n.classList.remove("page-end");
        n.style.paddingBottom = "";
      });
    };

    // Pagination is derived from element data (id/type/text) with the exact
    // PDF exporter algorithm, so it is independent of rendered widths. Gaps
    // are pure decoration and can't feed back into the computation.
    const measure = () => {
      const lines = Array.from(c.querySelectorAll(".script-line"));
      if (lines.length === 0) {
        endIdsRef.current = [];
        return { total: 1, list: [] };
      }
      const els = lines.map((n) => ({
        id: n.dataset.elementId,
        type: n.dataset.elementType || "action",
        text: n.textContent || "",
      }));
      const { total, pages } = paginatePrint(els);

      const endNodes = [];
      for (let i = 1; i < lines.length; i += 1) {
        if (pages[i] > pages[i - 1]) endNodes.push(lines[i - 1]);
      }

      // Clear stale gaps, then apply to the current page-end lines.
      c.querySelectorAll(".script-line.page-end").forEach((n) => {
        if (!endNodes.includes(n)) { n.classList.remove("page-end"); n.style.paddingBottom = ""; }
      });
      // padding-bottom gives real whitespace and never margin-collapses.
      endNodes.forEach((n) => { n.classList.add("page-end"); n.style.paddingBottom = `${GAP}px`; });
      endIdsRef.current = endNodes.map((n) => n.dataset.elementId);
      const list = endNodes.map((n, i) => ({ n: i + 2, top: n.offsetTop + n.offsetHeight - GAP / 2 }));
      return { total, list };
    };

    const currentPage = () => {
      const caret = getCurrentLineNode();
      if (!caret || !c.contains(caret)) return 1;
      const cTop = caret.offsetTop;
      let cur = 1;
      for (const id of endIdsRef.current) {
        const n = c.querySelector(`[data-element-id="${id}"]`);
        if (n && n.offsetTop < cTop) cur += 1;
      }
      return Math.min(totalRef.current, cur);
    };

    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const { total, list } = measure();
        totalRef.current = total;
        setBreaks(list);
        if (onPageInfo) onPageInfo({ total, current: currentPage() });
      });
    };

    recompute();

    // Content edits change pagination -> recompute. Style/padding mutations are
    // NOT observed, so adding gaps can't loop back into here.
    const mo = new MutationObserver(recompute);
    mo.observe(c, { childList: true, subtree: true, characterData: true });

    // Width changes (window resize, sidebar collapse) rewrap lines -> recompute.
    // Ignore height-only changes (those are our own gaps) so there's no loop.
    let lastW = c.clientWidth;
    const ro = new ResizeObserver(() => {
      const w = c.clientWidth;
      if (w === lastW) return;
      lastW = w;
      recompute();
    });
    ro.observe(c);

    const onSelChange = () => {
      if (onPageInfo) onPageInfo({ total: totalRef.current, current: currentPage() });
    };
    document.addEventListener("selectionchange", onSelChange);
    window.addEventListener("resize", recompute);
    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      ro.disconnect();
      document.removeEventListener("selectionchange", onSelChange);
      window.removeEventListener("resize", recompute);
      clearGaps();
    };
  }, [containerRef, onPageInfo]);

  return (
    <>
      {breaks.map((b) => (
        <div
          key={b.n}
          className="page-break"
          style={{ top: b.top }}
          data-testid={`page-break-${b.n}`}
        >
          <span className="page-break-label">Page {b.n}</span>
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers exported for callers
// ---------------------------------------------------------------------------
export function newBlankScript() {
  return [{ id: uid(), type: "scene_heading", text: "", locked: false }];
}

export { uid };

// Delete the contents of a non-collapsed selection (single or multi-element).
// Browser's native deleteContents() works inside contentEditable for us; we
// call it then let normalizeDom() (via handleInput) clean up.
function deleteSelectionContents(sel) {
  if (!sel.rangeCount) return;
  const r = sel.getRangeAt(0);
  if (r.collapsed) return;
  r.deleteContents();
}

// Escape a user query for safe use inside a RegExp.
function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Build a Range spanning [start,end] (plain-text offsets) within a single
// script line — used to paint Find matches via the CSS Custom Highlight API
// (no document-selection mutation, so the search box keeps focus).
function makeRange(lineNode, start, end) {
  if (!lineNode) return null;
  const walker = document.createTreeWalker(lineNode, NodeFilter.SHOW_TEXT);
  let acc = 0, startNode = null, startOff = 0, endNode = null, endOff = 0, node;
  while ((node = walker.nextNode())) {
    const len = node.textContent.length;
    if (startNode === null && acc + len >= start) { startNode = node; startOff = start - acc; }
    if (acc + len >= end) { endNode = node; endOff = end - acc; break; }
    acc += len;
  }
  if (!startNode || !endNode) return null;
  try {
    const range = document.createRange();
    range.setStart(startNode, startOff);
    range.setEnd(endNode, endOff);
    return range;
  } catch {
    return null;
  }
}
